import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useManageIPMSContext } from "./ManageIPMSContex";
import { GetEmpGoalAssessmentBymanagerResponse } from "./newipmsmodel";
import moment from "moment";
import { getCookieData } from "../../helper/api";
import { GoalsFlagListStatusEnum, GoalsFlagStatusEnumText } from "./constant";
import ReactDataTable from "./ReactDataTable";
import { useNavigate } from "react-router-dom";
import useIPMSApi from "./ipms";
import SingleSelect from "../../components/SingleSelect";

const STORAGE_KEY = "employeeGoalsFilters";

const getSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Error reading saved filters:", e);
  }
  return null;
};

const EmployeeGoalReviewPage: React.FC = () => {
  const { assessmentData } = useManageIPMSContext();
  const { fetchGetIpmsGetEmpAssessmentByManager } = useIPMSApi();
  const navigate = useNavigate();
  const currentUser = getCookieData("userData");
  const userDetail = currentUser && JSON.parse(currentUser);

  const savedState = React.useMemo(() => getSavedFilters(), []);

  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>(
    savedState?.activeSearchTerm ?? ""
  );
  const [activeStatusFilter, setActiveStatusFilter] = React.useState<string>(
    savedState?.activeStatusFilter ?? "SUBMITTED"
  );
  const [currentPage, setCurrentPage] = React.useState<number>(
    savedState?.currentPage ?? 1
  );
  const [pageSize, setPageSize] = React.useState<number>(
    savedState?.pageSize ?? 10
  );
  const [activeSortBy, setActiveSortBy] = React.useState<string>(
    savedState?.activeSortBy ?? "employee_name"
  );
  const [activeSortOrder, setActiveSortOrder] = React.useState<string>(
    savedState?.activeSortOrder ?? "asc"
  );

  const [pendingSortBy, setPendingSortBy] = React.useState<string>(
    savedState?.pendingSortBy ?? "employee_name"
  );
  const [pendingSortOrder, setPendingSortOrder] = React.useState<string>(
    savedState?.pendingSortOrder ?? "asc"
  );

  React.useEffect(() => {
    const filtersToSave = {
      activeSearchTerm,
      activeStatusFilter,
      currentPage,
      pageSize,
      activeSortBy,
      activeSortOrder,
      pendingSortBy,
      pendingSortOrder,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [
    activeSearchTerm,
    activeStatusFilter,
    currentPage,
    pageSize,
    activeSortBy,
    activeSortOrder,
    pendingSortBy,
    pendingSortOrder,
  ]);

  const assessmentYear = React.useMemo(() => {
    return assessmentData?.assessment_periods?.[0]
      ? moment(
          assessmentData.assessment_periods[0].start_assessment_date,
        ).year()
      : moment().year();
  }, [assessmentData]);

  const { data: goalData, isLoading } = useQuery({
    queryKey: [
      "managerGoals",
      userDetail?.employee_id,
      assessmentYear,
      activeSearchTerm,
      activeStatusFilter,
      currentPage,
      pageSize,
      activeSortBy,
      activeSortOrder,
    ],
    queryFn: () =>
      fetchGetIpmsGetEmpAssessmentByManager({
        employee_id: userDetail?.employee_id,
        assessment_year: 0,
        search_term: activeSearchTerm,
        department_id: 0,
        list_type: "GOAL",
        status_filter: activeStatusFilter,
        sort_by: activeSortBy,
        sort_order: activeSortOrder,
        page: currentPage,
        page_size: pageSize,
      }),
    enabled: !!userDetail?.employee_id && !!assessmentYear,
  });

  React.useEffect(() => {
    if (goalData?.pagination?.total !== undefined) {
      const total = goalData.pagination.total;
      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
    }
  }, [goalData, currentPage, pageSize]);

  const columns = [
    {
      name: "EmpName",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap pl-10">{row.full_name}</span>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "EmpId",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap">{row.employee_id}</span>
      ),
      sortable: true,
      center: true,
      width: "100px",
    },
    {
      name: "Manager",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap pl-10">{row.manager_name}</span>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Department",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap pl-10">{row.department}</span>
      ),
      sortable: false,
      width: "180px",
    },
    {
      name: "DOJ",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) =>
        moment(row.doj).isValid()
          ? moment(row.doj).format("DD MMM YYYY")
          : null,
      sortable: false,
      center: true,
      width: "120px",
    },
    // {
    //   name: "Duration",
    //   selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
    //     <span className=" text-wrap">
    //       {row.duration
    //         ? moment(row.duration.split(" to ")[0]).format("DD MMM YYYY")
    //         : ""}{" "}
    //       - <br />{" "}
    //       {row.duration
    //         ? moment(row.duration.split(" to ")[1]).format("DD MMM YYYY")
    //         : ""}
    //     </span>
    //   ),
    //   sortable: true,
    // },

    {
      name: "Year",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap">{row.year}</span>
      ),
      sortable: true,
      center: true,
      width: "120px",
    },
    // {
    //   name: "Quarter",
    //   selector: (row: GetEmpGoalAssessmentBymanagerResponse) => row.quarter,
    //   sortable: true,
    //   width: "90px",
    // },
    {
      name: "Submission On",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap">
          {moment(row.employee_submitted_at).isValid()
            ? moment(row.employee_submitted_at).format("DD MMM YYYY")
            : ""}
        </span>
      ),
      sortable: true,
      center: true,
      width: "140px",
    },
    {
      name: "Status",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => {
        const goal_status = row.goal_status;
        const statusText =
          GoalsFlagListStatusEnum[Number(goal_status)] ||
          GoalsFlagStatusEnumText[goal_status] ||
          GoalsFlagStatusEnumText[String(goal_status).toUpperCase()] ||
          goal_status;

        const getStatusClasses = () => {
          const normalizedStatus = String(statusText).toUpperCase();
          if (normalizedStatus === "DRAFT") {
            return "bg-gray-100 text-gray-800 dark:text-gray-400 border-gray-500";
          }
          if (normalizedStatus === "SUBMITTED") {
            return "bg-blue-100 text-blue-800 dark:text-blue-400 border-blue-400";
          }
          if (normalizedStatus === "APPROVED") {
            return "bg-green-100 text-green-800 dark:text-green-400 border-green-400";
          }
          if (normalizedStatus === "REJECTED") {
            return "bg-red-100 text-red-800 dark:text-red-400 border-red-400";
          }
          return "";
        };

        return (
          <span
            className={`${getStatusClasses()} border-0 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-gray-700`}
          >
            {statusText === "DRAFT" ? "SAVED" : statusText}
          </span>
        );
      },
      sortable: true,
      width: "120px",
      center: true,
    },
  ];

  const goalListData = React.useMemo(() => {
    return goalData?.items
      ? goalData.items.map((item) => {
          return {
            ...item,
            doj: moment(item["doj"]).isValid()
              ? moment(item["doj"]).format("DD MMM YYYY")
              : "",
            employee_submitted_at: moment(
              item["employee_submitted_at"],
            ).isValid()
              ? moment(item["employee_submitted_at"]).format("DD MMM YYYY")
              : "",
          };
        })
      : null;
  }, [goalData]);

  const handleEdit = (item: GetEmpGoalAssessmentBymanagerResponse) => {
    if (item.doj && item.employee_id && item.year) {
      const originalDate = moment(item.doj, "DD MMM YYYY");
      const yearstart = item.year;
      const updatedDate = originalDate.clone().year(yearstart);

      const startDate = updatedDate.clone();
      const endDate = updatedDate.clone().add(1, "year");

      navigate("/ipms_new/goal", {
        state: {
          formType: "ManageInput",
          employeeID: item.employee_id,
          employeeName: item.full_name,
          quarterName: "q" + item.quarter,
          start: startDate.format("YYYY-MM-DD"),
          end: endDate.format("YYYY-MM-DD"),
        },
      });
    }
  };

  const statusOptions = [
    { value: "SUBMITTED", label: "Employee Submitted" },
    { value: "APPROVED", label: "Manager Approved" },
    { value: "READY", label: "Employee Saved" },
    { value: "REJECTED", label: "Rejected" },
    { value: "ALL", label: "All Statuses" },
  ];

  const handleClearFilters = () => {
    setActiveSearchTerm("");
    setActiveStatusFilter("SUBMITTED");
    setCurrentPage(1);
    setActiveSortBy("employee_name");
    setActiveSortOrder("asc");
    setPendingSortBy("employee_name");
    setPendingSortOrder("asc");
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <span className="bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-gray-400 border border-gray-500">
        Employee Saved
      </span>
      <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-blue-400 border border-blue-400">
        Employee Submitted
      </span>
      <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-green-400 border border-green-400">
        Manager Approved
      </span>

      <p className="text-gray-500 text-sm mt-3">
        Note: Action buttons are only available when the date of join is
        present.
      </p>
      <ReactDataTable
        columns={columns}
        data={goalListData ?? []}
        onView={handleEdit}
        dateOfJoinAction={"doj"}
        headerColor={"green"}
        searchLabelName="Search By EmpName, EmpID"
        showFilter={true}
        useSearchButton={true}
        initialSearchValue={activeSearchTerm}
        paginationServer={true}
        paginationTotalRows={goalData?.pagination?.total ?? 0}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(size) => setPageSize(size)}
        onSearch={(text) => {
          setActiveSearchTerm(text);
          setActiveSortBy(pendingSortBy);
          setActiveSortOrder(pendingSortOrder);
          setCurrentPage(1);
        }}
        progressPending={isLoading}
        extraFilters={
          <div className="flex gap-3 items-end">
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Status
              </label>
              <SingleSelect
                value={
                  statusOptions.find((opt) => opt.value === activeStatusFilter) || {
                    value: "SUBMITTED",
                    label: "Employee Submitted",
                  }
                }
                onChange={(option: any) => {
                  setActiveStatusFilter(option?.value || "SUBMITTED");
                  setCurrentPage(1);
                }}
                options={statusOptions}
                placeholder="Status"
                className="mt-0 w-[180px]"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Sort By
              </label>
              <SingleSelect
                value={{
                  label:
                    pendingSortBy === "employee_id"
                      ? "Employee ID"
                      : pendingSortBy === "employee_name"
                        ? "Employee Name"
                        : pendingSortBy === "department"
                          ? "Department"
                          : pendingSortBy === "doj"
                            ? "Date of Joining"
                            : pendingSortBy === "quarter"
                              ? "Quarter"
                              : pendingSortBy === "year"
                                ? "Year"
                                : pendingSortBy === "employee_submitted_at"
                                  ? "Submission Date"
                                  : "Status",
                  value: pendingSortBy,
                }}
                onChange={(option: any) => setPendingSortBy(option?.value)}
                options={[
                  { value: "employee_id", label: "EmpID" },
                  { value: "employee_name", label: "EmpName" },
                  { value: "department", label: "Department" },
                  { value: "doj", label: "Date of Joining" },
                  { value: "quarter", label: "Quarter" },
                  { value: "year", label: "Year" },
                  { value: "employee_submitted_at", label: "Submission Date" },
                  { value: "goal_status", label: "Status" },
                ]}
                placeholder="Sort By"
                className="mt-0 w-[200px]"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Order
              </label>
              <SingleSelect
                value={{
                  label: pendingSortOrder === "asc" ? "Asc" : "Desc",
                  value: pendingSortOrder,
                }}
                onChange={(option: any) => setPendingSortOrder(option?.value)}
                options={[
                  { value: "asc", label: "Asc" },
                  { value: "desc", label: "Desc" },
                ]}
                placeholder="Order"
                className="mt-0 w-[100px]"
              />
            </div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 mb-0.5"
            >
              Clear
            </button>
          </div>
        }
      />
    </>
  );
};

export default EmployeeGoalReviewPage;
